import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, ArrowLeft, Inbox, Lock, LogOut, Search, Eye, EyeOff } from "lucide-react";
import { api } from "@/lib/api";
import AvailabilityPanel from "@/components/admin/AvailabilityPanel";
import AppointmentCard from "@/components/admin/AppointmentCard";
import { useAppointments } from "@/components/admin/useAppointments";

const inputClass = "w-full rounded-2xl border border-[var(--border)] bg-[var(--bg)] px-5 py-4 outline-none transition-colors focus:border-[var(--mint-dark)] focus:bg-white";

function Login({ onSuccess }) {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admin/login", { password });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--bg)" }} data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm rounded-[2rem] border border-[var(--border)] bg-white p-8 shadow-sm">
        <span className="flex h-12 w-12 items-center justify-center rounded-full text-white" style={{ background: "var(--mint-dark)" }}>
          <Lock size={20} />
        </span>
        <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tighter" style={{ color: "var(--text-dark)" }}>Panel de citas</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-muted)" }}>Ingresa la contraseña para continuar.</p>
        <div className="relative mt-6">
          <input data-testid="admin-password-input" type={showPassword ? "text" : "password"} className={inputClass + " pr-14"} placeholder="Contraseña"
            value={password} onChange={(e) => setPassword(e.target.value)} autoFocus />
          <button type="button" data-testid="toggle-password-visibility" onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
            className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors hover:text-[var(--mint-dark)]"
            style={{ color: "var(--text-muted)" }}>
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        </div>
        <button type="submit" disabled={loading} data-testid="admin-login-button"
          className="mt-4 w-full rounded-full px-6 py-4 text-base font-semibold text-white transition-transform hover:scale-[1.01] disabled:opacity-60"
          style={{ background: "var(--mint-dark)" }}>
          {loading ? "Verificando..." : "Entrar"}
        </button>
        <a href="/" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--mint-dark)" }}>
          <ArrowLeft size={16} /> Volver a la página
        </a>
      </form>
    </div>
  );
}

function DashboardHeader({ total, pending, loading, onRefresh, onLogout }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <a href="/" className="inline-flex items-center gap-2 text-sm font-semibold mb-3" style={{ color: "var(--mint-dark)" }} data-testid="admin-back-link">
          <ArrowLeft size={16} /> Volver a la página
        </a>
        <h1 className="font-display text-4xl font-extrabold tracking-tighter" style={{ color: "var(--text-dark)" }}>Solicitudes de cita</h1>
        <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>
          {total} en total · {pending} pendiente{pending === 1 ? "" : "s"}
        </p>
      </div>
      <div className="flex items-center gap-3">
        <button onClick={onRefresh} data-testid="admin-refresh-button"
          className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white transition-transform hover:scale-[1.03]"
          style={{ background: "var(--mint-dark)" }}>
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Actualizar
        </button>
        <button onClick={onLogout} data-testid="admin-logout-button"
          className="inline-flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-semibold transition-colors hover:bg-white"
          style={{ borderColor: "var(--border)", color: "var(--text-dark)" }}>
          <LogOut size={16} /> Salir
        </button>
      </div>
    </div>
  );
}

function AppointmentList({ loading, appointments, filtered, query, onToggle, onDelete, onSaved, onUnauthorized }) {
  if (loading) return <p className="text-center py-20" style={{ color: "var(--text-muted)" }}>Cargando...</p>;
  if (appointments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-[2rem] border border-[var(--border)] bg-white py-24 text-center" data-testid="admin-empty">
        <Inbox size={40} style={{ color: "var(--mint)" }} />
        <p className="mt-4 font-display text-xl font-bold" style={{ color: "var(--text-dark)" }}>Aún no hay citas</p>
        <p className="mt-1 text-sm" style={{ color: "var(--text-muted)" }}>Las solicitudes aparecerán aquí.</p>
      </div>
    );
  }
  if (filtered.length === 0) {
    return (
      <div className="rounded-[2rem] border border-[var(--border)] bg-white py-16 text-center" data-testid="search-empty">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Sin resultados para "{query}".</p>
      </div>
    );
  }
  return (
    <div className="grid gap-4">
      {filtered.map((a) => <AppointmentCard key={a.id} a={a} onToggle={onToggle} onDelete={onDelete} onSaved={onSaved} onUnauthorized={onUnauthorized} />)}
    </div>
  );
}

function Dashboard({ onLogout }) {
  const [query, setQuery] = useState("");
  const { appointments, loading, load, toggleStatus, remove } = useAppointments(onLogout);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? appointments.filter((a) => a.nombre.toLowerCase().includes(q) || a.servicio.toLowerCase().includes(q))
    : appointments;
  const pending = appointments.filter((a) => a.status !== "confirmada").length;

  return (
    <main className="min-h-screen px-6 py-10" style={{ background: "var(--bg)" }} data-testid="admin-page">
      <div className="mx-auto max-w-6xl">
        <DashboardHeader total={appointments.length} pending={pending} loading={loading} onRefresh={load} onLogout={onLogout} />
        <div className="mt-8">
          <AvailabilityPanel onUnauthorized={onLogout} />
          <div className="relative mb-6">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
            <input data-testid="search-input" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre o servicio..."
              className="w-full rounded-2xl border border-[var(--border)] bg-white pl-11 pr-4 py-3.5 outline-none transition-colors focus:border-[var(--mint-dark)]" />
          </div>
          <AppointmentList loading={loading} appointments={appointments} filtered={filtered} query={query} onToggle={toggleStatus} onDelete={remove}
            onSaved={load} onUnauthorized={onLogout} />
        </div>
      </div>
    </main>
  );
}

export default function Admin() {
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/admin/logout");
    } catch (err) {
      console.warn("logout: session already closed", err?.response?.status);
    }
    setAuthed(false);
  }, []);

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }} data-testid="admin-checking">
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Cargando...</p>
      </div>
    );
  }
  return authed ? <Dashboard onLogout={logout} /> : <Login onSuccess={() => setAuthed(true)} />;
}
